import React, { useState, useRef, useEffect } from 'react';
import { ChatInterface } from './ChatInterface';

interface FloatingChatModalProps {
  isOpen: boolean;
  onClose: () => void;
  testData: any;
  viewType: 'single' | 'trends';
  selectedDate?: string;
}

export const FloatingChatModal: React.FC<FloatingChatModalProps> = ({
  isOpen,
  onClose,
  testData,
  viewType,
  selectedDate
}) => {
  // Debug the testData being passed (only when opened)
  React.useEffect(() => {
    if (isOpen) {
      console.log('=== CHAT MODAL OPENED ===');
      console.log('testData keys:', Object.keys(testData || {}));
      console.log('viewType:', viewType);
    }
  }, [isOpen, testData, viewType]);
  const [position, setPosition] = useState({ x: window.innerWidth - 420, y: 100 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const modalRef = useRef<HTMLDivElement>(null);

  // Handle drag start
  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget || (e.target as HTMLElement).classList.contains('drag-handle')) {
      setIsDragging(true);
      setDragOffset({
        x: e.clientX - position.x,
        y: e.clientY - position.y
      });
    }
  };

  // Handle dragging
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (isDragging) {
        const newX = Math.max(0, Math.min(window.innerWidth - 400, e.clientX - dragOffset.x));
        const newY = Math.max(0, Math.min(window.innerHeight - 600, e.clientY - dragOffset.y));
        setPosition({ x: newX, y: newY });
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, dragOffset]);

  if (!isOpen) return null;

  return (
    <>
      {/* Modal - No backdrop so users can interact with the dashboard */}
      <div
        ref={modalRef}
        className="fixed bg-white rounded-lg shadow-2xl z-50 flex flex-col border border-gray-200"
        style={{
          left: `${position.x}px`,
          top: `${position.y}px`,
          width: '400px',
          height: '600px',
          maxHeight: '80vh',
          cursor: isDragging ? 'grabbing' : 'auto'
        }}
      >
        {/* Header */}
        <div 
          className="bg-gradient-to-r from-purple-600 to-blue-600 text-white p-4 rounded-t-lg flex items-center justify-between drag-handle"
          onMouseDown={handleMouseDown}
          style={{ cursor: 'grab' }}
        >
          <div className="flex items-center gap-3">
            {/* AI Sparkle Icon */}
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
            </svg>
            <h2 className="text-lg font-semibold">AI Lab Assistant</h2>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-white hover:bg-opacity-20 rounded transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Disclaimer */}
        <div className="bg-amber-50 border-b border-amber-200 px-4 py-2">
          <p className="text-xs text-amber-800">
            <strong>Important:</strong> This AI assistant provides general information only. 
            Always consult with your healthcare provider for medical interpretation and advice.
          </p>
        </div>

        {/* Chat Content */}
        <div className="flex-1 overflow-hidden">
          <ChatInterface
            context={testData}
            viewType={viewType}
            selectedDate={selectedDate}
          />
        </div>
      </div>
    </>
  );
};